import {
  Box,
  Button,
  Chip,
  Grid,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { startOfDay } from "date-fns";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormCombos } from "../../../../hooks/useFormCombos";
import { getCommittees } from "../../../../services/committees.service";
import { getInputDateFormats } from "../../../../utils/get-date-time-locale";
import { Select } from "../../../Common/Select";

import {
  ButtonsContainer,
  Content,
  FieldsContainer,
  HeaderContainer,
} from "./styles";

const useStyles = makeStyles((theme) => ({
  button: {
    fontSize: "1rem",
    fontWeight: "bold",
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
    minWidth: 180,
    minHeight: 50,
  },
  buttonOutlined: {
    border: "2px solid",
    fontSize: "1rem",
    fontWeight: "bold",
    color: theme.palette.grey.main,
    textTransform: "capitalize",
    minWidth: 180,
    minHeight: 50,
  },
  chip: {
    margin: 2,
  },
}));

function FilterDrawer({ onClose, t, filters, setFilters, actionShowSpinner }) {
  const classes = useStyles();
  const { i18n } = useTranslation();

  const [committeesOptions, setCommitteesOptions] = useState([]);
  const [roundRobinOptions, setRoundRobinOptions] = useState([
    { id: true, name: t("forms.dialog.yes") },
    { id: false, name: t("forms.dialog.no") },
  ]);
  const [committee, setCommittee] = useState(filters.committee);
  const [status, setStatus] = useState(filters.status);
  const [startDate, setStartDate] = useState(filters.startDate);
  const [endDate, setEndDate] = useState(filters.endDate);
  const [roundRobin, setRoundRobin] = useState(filters.roundRobin);

  const { status: meetStatusOptions, getStatus: getMeetStatusOptions } =
    useFormCombos();

  useEffect(() => {
    const language = localStorage.getItem("i18nextLng");
   
    const loadCommitteeOptions = async () => {
      const response = await getCommittees({
        showDisabled: true,
        pageSize: 999999
      });
      
      setCommitteesOptions(
        response.data.items
      );
    };
    actionShowSpinner(true);

    loadCommitteeOptions();

    getMeetStatusOptions(language);

    actionShowSpinner(false);
  }, []);

  useEffect(() => {
    setCommittee(filters?.committee);
    setStatus(filters?.status);
    setStartDate(filters?.startDate);
    setEndDate(filters?.endDate);
    setRoundRobin(filters.roundRobin);
  }, [filters]);

  const handleApplyFilters = () => {
    setFilters({
      ...(committee && { committee }),
      ...(status && { status }),
      ...(startDate ? { startDate } : { startDate: null }),
      ...(endDate ? { endDate } : { endDate: null }),
      ...(roundRobin && { roundRobin }),
    });

    onClose();
  };

  const handleCleanFilters = () => {
    setFilters({
      committee: undefined,
      status: undefined,
      startDate: null,
      endDate: null,
      keyword: "",
      roundRobin: undefined,
    });

    onClose();
  };

  return (
    <Box sx={{ width: 460, flex: 1, display: "flex" }} role="presentation">
      <Content>
        <HeaderContainer>
          <Typography variant="h4">{t("forms.filters")}</Typography>

          <Close
            onClick={() => {
              onClose();
            }}
          />
        </HeaderContainer>

        <FieldsContainer>
          <Grid container spacing={3}>
            <Grid item lg={12} xs={12}>
              <Select
                id="committee"
                options={committeesOptions}
                value={committee}
                label={t("slots.committee")}
                setValue={(value) => {
                  setCommittee(value);
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                fullWidth
                id="status"
                options={meetStatusOptions}
                value={status}
                label={t("forms.status")}
                setValue={(values) => {
                  setStatus(values);
                }}
                multiple={true}
                renderTags={(selectedValues) => {
                  return selectedValues.map((option) => (
                    <Chip
                      className={classes.chip}
                      key={option.id}
                      onDelete={() => {
                        setStatus(status.filter((v) => v.id !== option.id));
                      }}
                      onClick={() => {
                        setStatus(status.filter((v) => v.id !== option.id));
                      }}
                      variant="outlined"
                      label={option.name}
                    />
                  ));
                }}
              />
            </Grid>

            <Grid item lg={6} xs={6}>
              <KeyboardDatePicker
                autoOk
                disableToolbar
                variant="inline"
                format={getInputDateFormats(i18n.language)}
                fullWidth
                label={t("forms.startDateLabel")}
                value={startDate}
                onChange={(date) =>
                  setStartDate(date ? startOfDay(date) : null)
                }
                KeyboardButtonProps={{
                  "aria-label": "change date",
                }}
              />
            </Grid>

            <Grid item lg={6} xs={6}>
              <KeyboardDatePicker
                autoOk
                disableToolbar
                variant="inline"
                format={getInputDateFormats(i18n.language)}
                fullWidth
                label={t("forms.endDateLabel")}
                value={endDate}
                onChange={(date) => setEndDate(date ? startOfDay(date) : null)}
                KeyboardButtonProps={{
                  "aria-label": "change date",
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                id="roundRobin"
                options={roundRobinOptions}
                value={roundRobin}
                label={t("meeting.roundRobin")}
                setValue={(value) => {
                  setRoundRobin(value);
                }}
              />
            </Grid>
          </Grid>
        </FieldsContainer>

        <ButtonsContainer>
          <Button
            variant="contained"
            onClick={handleCleanFilters}
            className={classes.buttonOutlined}
          >
            {t("forms.cleanFilters")}
          </Button>

          <Button
            variant="contained"
            onClick={handleApplyFilters}
            className={classes.button}
          >
            {t("forms.applyFilters")}
          </Button>
        </ButtonsContainer>
      </Content>
    </Box>
  );
}

export default FilterDrawer;
